import { useLayoutEffect, useRef, useState } from "react";

export interface SegmentedTabItem {
  value: string;
  label: string;
  badge?: number | undefined;
}

interface SegmentedTabsProps {
  items: SegmentedTabItem[];
  value: string;
  onChange: (value: string) => void;
  /**
   * "md" (default) is the page-level tab strip Attendance uses. "sm" is the
   * same pills shrunk to sit in a filter/toolbar row next to 36px controls,
   * where the full size reads as a second heading.
   */
  size?: "sm" | "md";
}

const SIZES = {
  md: { pad: 4, btn: "7px 16px", font: 13, badge: 18, badgeFont: 11 },
  sm: { pad: 3, btn: "6px 14px", font: 12, badge: 16, badgeFont: 10 },
} as const;

interface IndicatorRect { left: number; top: number; width: number; height: number }

export function SegmentedTabs({ items, value, onChange, size = "md" }: SegmentedTabsProps) {
  const s = SIZES[size];
  const containerRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);

  useLayoutEffect(() => {
    function measure() {
      const container = containerRef.current;
      const btn = btnRefs.current[value];
      if (!container || !btn) return;
      const containerRect = container.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      setIndicator({
        left: btnRect.left - containerRect.left,
        top: btnRect.top - containerRect.top,
        width: btnRect.width,
        height: btnRect.height,
      });
    }
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [value, items, size]);

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-flex", flexWrap: "wrap", gap: 2, padding: s.pad, background: "var(--color-surface-100)", borderRadius: "var(--radius-full)" }}>
      {indicator && (
        <div style={{
          position: "absolute", left: indicator.left, top: indicator.top, width: indicator.width, height: indicator.height,
          background: "var(--color-brand-600)", borderRadius: "var(--radius-full)",
          transition: "left 0.25s cubic-bezier(0.4,0,0.2,1), top 0.25s cubic-bezier(0.4,0,0.2,1), width 0.25s cubic-bezier(0.4,0,0.2,1)",
        }} />
      )}
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            ref={(el) => { btnRefs.current[item.value] = el; }}
            onClick={() => onChange(item.value)}
            style={{
              position: "relative", zIndex: 1,
              display: "flex", alignItems: "center", gap: 6,
              padding: s.btn, border: "none", borderRadius: "var(--radius-full)",
              fontSize: s.font, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
              background: "transparent",
              color: active ? "#fff" : "var(--color-surface-700)",
              transition: "color 0.2s ease",
            }}
          >
            {item.label}
            {!!item.badge && (
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                minWidth: s.badge, height: s.badge, padding: "0 5px", borderRadius: "var(--radius-full)",
                fontSize: s.badgeFont, fontWeight: 700,
                background: active ? "rgba(255,255,255,0.25)" : "var(--color-warning-bg)",
                color: active ? "#fff" : "var(--color-warning-fg)",
                transition: "background 0.2s ease, color 0.2s ease",
              }}>
                {item.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
